import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { apiRequests } from '../../shared/api/apiRequests.js';
import { LanguageOptions } from '../../entities/LanguageOptions/index.js';
import { Availability } from '../../entities/Availability/index.js';
import { toast } from 'react-toastify';
import { hasChangesAvailability } from '../../shared/utils/compareAvailability.js';
import { SEOMetatagsForm } from '../../shared/ui/SEOMetatagsForm';
import { UploadIcon } from '../../features/UploadIcon/ui/UploadIcon';
import { Form, Button, Row, Col } from 'react-bootstrap';
import { Guid } from 'js-guid';
import { FormLayout, FormSection, FormField, FormLoading } from '../../shared/ui/FormLayout';

const EditVacancyPage = () => {
  const [currencies, setCurrencies] = useState([]);
  const [categories, setCategories] = useState([]);
  const [form, setForm] = useState(null);
  const [oldData, setOldData] = useState(null);
  const [skillInput, setSkillInput] = useState('');
  const [logoImg, setLogoImg] = useState(null);
  const [saving, setSaving] = useState(false);
  const { id } = useParams();
  const navigate = useNavigate();

  useEffect(() => {
    const getData = async () => {
      const vacancyData = await apiRequests.hr.getById(id);
      console.log('Данные вакансии с сервера:', vacancyData);
      
      // Нормализация данных для корректного отображения в компоненте
      const normalizedData = {...vacancyData};
      
      // Обработка SEO метатегов (регистр может отличаться)
      console.log('Исходные данные SEO метатегов:', normalizedData.seoMetatags);
      
      // Обеспечиваем наличие объекта seoMetatags в данных
      if (!normalizedData.seoMetatags) {
        normalizedData.seoMetatags = {
          title: '',
          description: '',
          keywords: ''
        };
      } else {
        // Если объект есть, но нет полей, добавляем их с пустыми значениями
        normalizedData.seoMetatags.title = normalizedData.seoMetatags.title || '';
        normalizedData.seoMetatags.description = normalizedData.seoMetatags.description || '';
        normalizedData.seoMetatags.keywords = normalizedData.seoMetatags.keywords || '';
      }
      
      console.log('Обработанные SEO метатеги:', normalizedData.seoMetatags);
      
      // Обработка навыков (регистр может отличаться)
      console.log('Навыки с сервера:', normalizedData.skills);
      
      // Проверяем и обрабатываем навыки в разных регистрах
      if (normalizedData.Skills && !normalizedData.skills) {
        normalizedData.skills = normalizedData.Skills;
      } else if (!normalizedData.skills) {
        normalizedData.skills = [];
      }
      
      // Преобразуем формат навыков, чтобы убедиться, что каждый навык имеет поля title и id
      if (normalizedData.skills && normalizedData.skills.length > 0) {
        // Проверяем, есть ли у навыка поле title с маленькой буквы
        normalizedData.skills = normalizedData.skills.map(skill => {
          if (skill.Title && !skill.title) {
            return { ...skill, title: skill.Title, id: skill.id || skill.Id || Date.now() };
          } else if (!skill.title && !skill.Title) {
            return { ...skill, title: '', id: skill.id || skill.Id || Date.now() };
          }
          return { ...skill, id: skill.id || skill.Id || Date.now() };
        });
      }
      
      // Обработка цвета фона
      if (normalizedData.ImgBackgroundColorHex && !normalizedData.imgBackgroundColorHex) {
        normalizedData.imgBackgroundColorHex = normalizedData.ImgBackgroundColorHex;
      } else if (!normalizedData.imgBackgroundColorHex) {
        normalizedData.imgBackgroundColorHex = '#FFFFFF';
      }
      
      // Обработка пути к иконке
      console.log('Путь к логотипу:', normalizedData.imgPath);
      
      // Устанавливаем путь к логотипу для отображения в предпросмотре
      normalizedData.logoImgUrl = normalizedData.imgPath || null;
      console.log('Установленный путь к логотипу:', normalizedData.logoImgUrl);
      
      const categories = await apiRequests.hr.getCategories();
      setCategories(categories);
      console.log('Обработанные данные для формы:', normalizedData);
      setForm(normalizedData);
      setOldData(normalizedData);
      const currencies = await apiRequests.currencies.get();
      setCurrencies(currencies);
    };
    getData();
  }, []);
  
  // Добавление навыка
  const addSkill = () => {
    if (!skillInput.trim() || !form) return;
    
    setForm(prev => ({
      ...prev,
      skills: [...(prev.skills || []), { id: Guid.newGuid().toString(), title: skillInput.trim() }]
    }));
    setSkillInput('');
  };

  // Удаление навыка
  const removeSkill = (skillId) => {
    if (!form) return;
    
    setForm(prev => ({
      ...prev,
      skills: (prev.skills || []).filter(skill => skill.id !== skillId)
    }));
  };
  
  // Обработка нажатия Enter при вводе навыка
  const handleSkillInputKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addSkill();
    }
  };
  
  // Обработка загрузки иконки
  const handleLogoUpload = (blob) => {
    setLogoImg(blob);
  };

  const handleRequest = async () => {
    if (hasChangesAvailability(oldData.availability, form.availability)) {
      await apiRequests.hr.updateAvailability(form.availability);
    }

    try {
      // Копируем форму для обработки
      const updatedForm = { ...form };
      
      // Преобразуем навыки в формат, ожидаемый сервером (без id)
      if (updatedForm.skills || updatedForm.Skills) {
        // Создаем массив навыков из имеющихся данных
        const skillsArray = updatedForm.skills || updatedForm.Skills || [];
        
        console.log('Навыки перед отправкой:', skillsArray);
        
        // Преобразуем в формат без id, только с полем title
        updatedForm.Skills = skillsArray.map(skill => ({
          title: skill.title || skill.Title
        }));
        
        // Удаляем оригинальный массив skills с id
        delete updatedForm.skills;
      } else {
        // Если навыков нет, отправляем пустой массив
        updatedForm.Skills = [];
      }
      
      // Убедимся, что цвет фона начинается с #
      if (updatedForm.imgBackgroundColorHex && !updatedForm.imgBackgroundColorHex.startsWith('#')) {
        updatedForm.imgBackgroundColorHex = `#${updatedForm.imgBackgroundColorHex}`;
      }
      
      // Создаем FormData и добавляем все поля как ожидает API
      const formData = new FormData();
      
      // Добавляем файл логотипа, если он есть
      if (logoImg) {
        // Создаем новый файл с правильным именем и расширением
        const fileType = logoImg.type || 'image/png';
        const fileExt = fileType.split('/')[1] || 'png';
        const fileName = `vacancy_logo_${Date.now()}.${fileExt}`;
        
        // Создаем новый файл с правильным именем
        const fileWithName = new File([logoImg], fileName, { type: fileType });
        
        formData.append('logoImg', fileWithName);
      }
      
      // Добавляем основные данные как строку JSON
      formData.append('model', JSON.stringify(updatedForm));
      
      // Детальное логирование для отладки
      console.log('Отправляемые данные:', updatedForm);
      console.log('Отправляемые Skills:', updatedForm.Skills);
      console.log('Отправляемые seoMetatags:', updatedForm.seoMetatags);
      
      // Отправляем запрос с тоаст-уведомлением
      toast.promise(
        apiRequests.hr.updateVacancy(formData), 
        {
          pending: 'Запрос к серверу',
          error: 'Произошла ошибка',
          success: 'Вакансия успешно изменена',
        }
      );
    } catch (error) {
      console.error('Ошибка при подготовке данных:', error);
      toast.error('Ошибка при подготовке данных');
      return;
    }
  };

  if (!form) return <FormLoading />;

  return (
    <FormLayout
      onSubmit={handleRequest}
      onBack={() => navigate('/hr/vacancies')}
      saving={saving}
      submitText="Сохранить"
      savingText="Сохранение..."
    >
      <FormSection title="Основная информация">
        <Row>
          <Col lg={8}>
            <FormField label="Название" required>
              <Form.Control
                type="text"
                value={form.title}
                onChange={(e) => setForm((prev) => ({ ...prev, title: e.target.value }))}
                placeholder="Введите название вакансии"
              />
            </FormField>

            <Row>
              <Col md={4}>
                <FormField label="Ставка от">
                  <Form.Control
                    type="number"
                    value={form.salaryFrom}
                    onChange={(e) => setForm((prev) => ({ ...prev, salaryFrom: e.target.value }))}
                  />
                </FormField>
              </Col>
              <Col md={4}>
                <FormField label="Ставка до">
                  <Form.Control
                    type="number"
                    value={form.salaryTo}
                    onChange={(e) => setForm((prev) => ({ ...prev, salaryTo: e.target.value }))}
                  />
                </FormField>
              </Col>
              <Col md={4}>
                <FormField label="Валюта">
                  <Form.Select
                    value={form.currencyId}
                    onChange={(e) => setForm((prev) => ({ ...prev, currencyId: Number(e.target.value) }))}
                  >
                    {currencies?.items?.map((c) => (
                      <option key={c.id} value={c.id}>{c.title}</option>
                    ))}
                  </Form.Select>
                </FormField>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <FormField label="Категория">
                  <Form.Select
                    value={form.categoryId}
                    onChange={(e) => setForm((prev) => ({ ...prev, categoryId: Number(e.target.value) }))}
                  >
                    {categories?.items?.map((c) => (
                      <option key={c.id} value={c.id}>{c.title}</option>
                    ))}
                  </Form.Select>
                </FormField>
              </Col>
              <Col md={3}>
                <FormField label="Опыт от (лет)">
                  <Form.Control
                    type="number"
                    value={form.expierence?.yearsFrom}
                    onChange={(e) => setForm((prev) => ({
                      ...prev,
                      expierence: { ...prev.expierence, yearsFrom: e.target.value }
                    }))}
                  />
                </FormField>
              </Col>
              <Col md={3}>
                <FormField label="Опыт до (лет)">
                  <Form.Control
                    type="number"
                    value={form.expierence?.yearsTo || ''}
                    onChange={(e) => setForm((prev) => ({
                      ...prev,
                      expierence: { ...prev.expierence, yearsTo: e.target.value || null }
                    }))}
                  />
                </FormField>
              </Col>
            </Row>
          </Col>

          <Col lg={4}>
            <FormField label="Иконка вакансии">
              <UploadIcon 
                onSelect={handleLogoUpload} 
                initialIcon={form.logoImgUrl || null} 
                entityType="vacancy"
              />
            </FormField>
            <FormField label="Цвет фона иконки">
              <div className="d-flex align-items-center">
                <input
                  type="color"
                  value={form.imgBackgroundColorHex || '#FFFFFF'}
                  onChange={(e) => setForm(prev => ({ ...prev, imgBackgroundColorHex: e.target.value }))}
                  className="me-2"
                  style={{ width: '40px', height: '38px' }}
                />
                <Form.Control
                  type="text"
                  value={form.imgBackgroundColorHex || '#FFFFFF'}
                  onChange={(e) => setForm(prev => ({ ...prev, imgBackgroundColorHex: e.target.value }))}
                  placeholder="#FFFFFF"
                />
              </div>
            </FormField>
          </Col>
        </Row>
      </FormSection>

      <FormSection title="Ключевые навыки">
        <div className="d-flex mb-3">
          <Form.Control
            type="text"
            value={skillInput}
            onChange={(e) => setSkillInput(e.target.value)}
            onKeyDown={handleSkillInputKeyDown}
            placeholder="Введите навык и нажмите +"
            className="me-2"
            style={{ maxWidth: '300px' }}
          />
          <Button variant="outline-primary" onClick={addSkill}>+</Button>
        </div>
        <div>
          {form.skills?.map(skill => (
            <span key={skill.id || skill.title} className="badge bg-primary me-2 mb-2 p-2">
              {skill.title}
              <Button variant="link" className="p-0 ms-2 text-white" onClick={() => removeSkill(skill.id || skill.title)}>✕</Button>
            </span>
          ))}
          {(!form.skills || form.skills.length === 0) && <em className="text-muted">Навыки не добавлены</em>}
        </div>
      </FormSection>

      <FormSection title="Настройки">
        <Row>
          <Col lg={6}>
            <LanguageOptions
              initialValue={form.mainLanguageId}
              onSelect={(val) => setForm((prev) => ({ ...prev, mainLanguageId: val }))}
            />
          </Col>
        </Row>

        <div className="mt-4">
          <Availability
            initialValues={form.availability}
            onSelect={(val) =>
              setForm((prev) => ({
                ...prev,
                availability: { ...prev.availability, availableInAllCountries: val },
              }))
            }
            onSelectAllowed={(val) =>
              setForm((prev) => ({
                ...prev,
                availability: { ...prev.availability, allowedCountriesIds: val },
              }))
            }
            onSelectDisallowed={(val) =>
              setForm((prev) => ({
                ...prev,
                availability: { ...prev.availability, disallowedCountriesIds: val },
              }))
            }
          />
        </div>
      </FormSection>

      <FormSection title="SEO настройки">
        <SEOMetatagsForm
          seoMetatags={form.seoMetatags}
          onChange={(metaTags) => {
            setForm((prev) => ({ 
              ...prev, 
              SEOMetatags: metaTags,
              seoMetatags: {
                title: metaTags.Title || metaTags.title || '',
                description: metaTags.Description || metaTags.description || '',
                keywords: metaTags.Keywords || metaTags.keywords || ''
              }
            }));
          }}
        />
      </FormSection>
    </FormLayout>
  );
};

export default EditVacancyPage;
